import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaHome, FaFileAlt, FaEnvelope, FaSignOutAlt, 
  FaChartLine, FaEye, FaEdit, FaClock, FaCoins,
  FaBars, FaTimes
} from 'react-icons/fa';
import { useAuth, api } from '../../App';
import './Admin.css';

const AdminLayout = ({ children, title }) => {
  const { logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar when route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin', icon: <FaHome />, label: 'Nadzorna plošča' },
    { path: '/admin/posts', icon: <FaFileAlt />, label: 'Objave' },
    { path: '/admin/content', icon: <FaEdit />, label: 'Vsebina strani' },
    { path: '/admin/contacts', icon: <FaEnvelope />, label: 'Sporočila' },
    { path: '/admin/fund-data', icon: <FaCoins />, label: 'FCF Podatki' },
  ];

  return (
    <div className="admin-layout">
      {/* Mobile overlay */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <div className="logo-icon">
              <span>FM</span>
            </div>
            <span className="logo-text">Upravljanje</span>
          </Link>
          <button 
            className="sidebar-close-btn"
            onClick={() => setSidebarOpen(false)}
            aria-label="Zapri meni"
          >
            <FaTimes />
          </button>
        </div>
        
        <nav className="sidebar-nav">
          <div className="nav-section">
            <span className="nav-section-title">Meni</span>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
          
          <div className="nav-section">
            <span className="nav-section-title">Hitre povezave</span>
            <a href="/" target="_blank" rel="noopener noreferrer" className="nav-item">
              <FaEye />
              <span>Ogled spletne strani</span>
            </a>
          </div>
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-info" style={{ padding: '0 0 12px 0', fontSize: '13px', color: 'var(--text-muted)' }}>
            {user?.email}
          </div>
          <button onClick={handleLogout} className="logout-btn">
            <FaSignOutAlt />
            <span>Odjava</span>
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-header">
          <button 
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Odpri meni"
          >
            <FaBars />
          </button>
          <h1>{title}</h1>
        </header>
        <div className="admin-content">
          {children}
        </div>
      </main>
    </div>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsData, postsData] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/posts?limit=5')
      ]);
      setStats(statsData);
      setRecentPosts(postsData.posts || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('sl-SI', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <AdminLayout title="Nadzorna plošča">
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Nalaganje...</p>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="dashboard-stats">
            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="stat-card-header">
                <div className="stat-icon">
                  <FaFileAlt />
                </div>
              </div>
              <div className="stat-label">Skupaj objav</div>
              <div className="stat-value">{stats?.totalPosts || 0}</div>
            </motion.div>

            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              <div className="stat-card-header">
                <div className="stat-icon">
                  <FaChartLine />
                </div>
              </div>
              <div className="stat-label">Objavljeno</div>
              <div className="stat-value">{stats?.publishedPosts || 0}</div>
            </motion.div>

            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              <div className="stat-card-header">
                <div className="stat-icon">
                  <FaClock />
                </div>
              </div>
              <div className="stat-label">Osnutki</div>
              <div className="stat-value">{stats?.draftPosts || 0}</div>
            </motion.div>

            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              <div className="stat-card-header">
                <div className="stat-icon">
                  <FaEye />
                </div>
              </div>
              <div className="stat-label">Skupaj ogledov</div>
              <div className="stat-value">{stats?.totalViews || 0}</div>
            </motion.div>
          </div>

          {/* Recent Posts */}
          <motion.div
            className="posts-table"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.3 }}
          >
            <div className="posts-table-header">
              <h2>Nedavne objave</h2>
              <Link to="/admin/posts" className="btn btn-ghost">Poglej vse</Link>
            </div>
            
            {recentPosts.length === 0 ? (
              <div className="empty-state">
                <h3>Še ni objav</h3>
                <p>Ustvarite prvo objavo za začetek.</p>
                <Link to="/admin/posts/new" className="btn btn-primary">Ustvari objavo</Link>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Naslov</th>
                      <th>Kategorija</th>
                      <th>Status</th>
                      <th>Datum</th>
                      <th>Ogledi</th>
                      <th>Akcije</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPosts.map((post) => (
                      <tr key={post.id}>
                        <td className="post-title-cell">
                          <Link to={`/admin/posts/${post.id}`} className="post-title-link">
                            {post.title}
                          </Link>
                        </td>
                        <td>
                          <span className="category-tag">{post.category}</span>
                        </td>
                        <td>
                          <span className={`status-badge ${post.status}`}>
                            {post.status}
                          </span>
                        </td>
                        <td>{formatDate(post.created_at)}</td>
                        <td>{post.views || 0}</td>
                        <td>
                          <div className="action-buttons">
                            <Link to={`/admin/posts/${post.id}`} className="action-btn">
                              <FaEdit />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AdminLayout>
  );
};

export { AdminLayout };
export default AdminDashboard;

